/**
 * Información de versiones de plataforma + componentes.
 * Se registra en consola y se reutiliza en UI (modo soporte / diálogo).
 */

import { normalizeVersionInfoSpacing } from './version-info-display.js';
import { appendSupportConfigToVersionText } from './support-config-snapshot.js';

let _cachedText = null;

export function isLikelyHtmlResponse(text) {
  const sample = (text || '').trimStart().slice(0, 120).toLowerCase();
  return sample.startsWith('<!doctype') || sample.startsWith('<html') || sample.includes('<head>');
}

export function isValidVersionInfoBody(text) {
  if (!text || isLikelyHtmlResponse(text)) return false;
  return text.includes('=== TRAZiT COMPONENTS ===') || text.includes('=== MAIN PROJECT ===');
}

/**
 * Consulta el endpoint /health del backend para obtener timestamp y transactionMode.
 * Falla en silencio (devuelve null) si no hay backendUrl, CORS bloquea o el backend no responde.
 * @param {string} backendUrl - p. ej. https://platform.trazit.net:8443/TRAZiT-1.3.0
 * @returns {Promise<object|null>}
 */
export async function fetchBackendHealth(backendUrl) {
  const base = String(backendUrl || '').trim().replace(/\/+$/, '');
  if (!base) return null;
  try {
    const response = await fetch(`${base}/health`, { cache: 'no-store' });
    if (!response.ok) return null;
    const data = await response.json();
    return data && typeof data === 'object' ? data : null;
  } catch (_) {
    return null;
  }
}

/**
 * Zona TRAZiT Testing entre DEPLOYMENT INFO y MAIN PROJECT (rama UIT de config.json).
 * El valor es de runtime; no se escribe en version_info.txt del bucket.
 */
export function injectTrazitTestingSection(versionText, config = {}) {
  const raw = config.uiTestingBranch;
  const branch = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '---';
  const block = `=== TRAZiT TESTING ===\nuiTestingBranch: ${branch}\n`;
  const text = versionText || '';
  if (text.includes('=== TRAZiT TESTING ===')) {
    return text.replace(
      /=== TRAZiT TESTING ===\r?\n(?:uiTestingBranch: [^\n]*\r?\n)?/,
      block,
    );
  }
  if (text.includes('=== MAIN PROJECT ===')) {
    return text.replace(/=== MAIN PROJECT ===/, `${block}\n=== MAIN PROJECT ===`);
  }
  return `${text.replace(/\s*$/, '\n\n')}${block}`;
}

export function buildPlatformInfoBlock(config = {}, health = null) {
  let backendWarVersion = '---';
  try {
    const m = String(config.backendUrl || '').match(/\/(TRAZiT-[^/?#]+)/);
    if (m) backendWarVersion = m[1];
  } catch (_) { /* noop */ }

  const buildDate = health?.timestamp
    ? String(health.timestamp)
    : '--- (backend /health no disponible)';

  const lines = [
    '=== TRAZiT PLATFORM INFO ===',
    `Frontend release date: ${config.releaseDate || '---'}`,
    `Database: ${config.dbName || '---'}`,
    `Backend URL: ${config.backendUrl || '---'}`,
    `Backend WAR version: ${backendWarVersion}`,
    `Backend WAR build date: ${buildDate}`,
    `proceduresMenuVersion: ${config.proceduresMenuVersion || '---'}`,
    `useFakeProceduresList: ${config.useFakeProceduresList || '---'}`,
  ];
  if (health) {
    if (health.service) lines.push(`Backend service: ${health.service}`);
    if (health.version) lines.push(`Backend service version: ${health.version}`);
    if (health.status) lines.push(`Backend status: ${health.status}`);

    if (health.database) lines.push(`Database status: ${health.database}`);
    const jdbc = health.jdbcDriver;
    if (jdbc && typeof jdbc === 'object') {
      if (jdbc.dbProduct) lines.push(`Database product: ${jdbc.dbProduct}`);
      if (jdbc.dbVersion) lines.push(`Database version: ${jdbc.dbVersion}`);
      if (jdbc.name) lines.push(`JDBC driver: ${jdbc.name}`);
      if (jdbc.version) lines.push(`JDBC driver version: ${jdbc.version}`);
    }

    const tm = health.transactionMode;
    if (tm && typeof tm === 'object') {
      lines.push(`transactionMode.featureEnabled: ${tm.featureEnabled}`);
      lines.push(`transactionMode.defaultMode: ${tm.defaultMode}`);
      if (tm.bootstrapVersion) {
        lines.push(`transactionMode.bootstrapVersion: ${tm.bootstrapVersion}`);
      }
    }
  }

  lines.push('');
  return lines.join('\n');
}

export async function fetchPlatformVersionInfoText(config = {}, { forceRefresh = false } = {}) {
  if (!forceRefresh && _cachedText && !isLikelyHtmlResponse(_cachedText)) {
    return appendSupportConfigToVersionText(injectTrazitTestingSection(_cachedText, config), config);
  }
  if (forceRefresh) _cachedText = null;

  const health = await fetchBackendHealth(config.backendUrl);
  const platformBlock = buildPlatformInfoBlock(config, health);
  try {
    const response = await fetch('/components_info/version_info.txt', { cache: 'no-store' });
    if (response.ok) {
      const versionInfo = await response.text();
      if (isValidVersionInfoBody(versionInfo)) {
        const merged = injectTrazitTestingSection(
          normalizeVersionInfoSpacing(`${platformBlock.trimEnd()}\n\n${versionInfo.trimStart()}`),
          config,
        );
        _cachedText = appendSupportConfigToVersionText(merged, config);
      } else {
        _cachedText = appendSupportConfigToVersionText(
          injectTrazitTestingSection(
            `${platformBlock}=== TRAZiT COMPONENTS ===\nNo disponible (en local reinicia npm run dev para regenerar version_info)\n`,
            config,
          ),
          config,
        );
      }
    } else {
      _cachedText = appendSupportConfigToVersionText(
        injectTrazitTestingSection(
          `${platformBlock}=== TRAZiT COMPONENTS ===\nNo disponible (HTTP ${response.status})\n`,
          config,
        ),
        config,
      );
    }
  } catch (error) {
    _cachedText = appendSupportConfigToVersionText(
      injectTrazitTestingSection(
        `${platformBlock}=== TRAZiT COMPONENTS ===\nError al cargar - ${error.message}\n`,
        config,
      ),
      config,
    );
  }
  return _cachedText;
}

/** Carga, cachea y escribe en consola (warn + log para compatibilidad con herramientas). */
export async function loadAndLogPlatformVersionInfo(config = {}) {
  if (_cachedText && isLikelyHtmlResponse(_cachedText)) {
    invalidatePlatformVersionInfoCache();
  }
  const text = await fetchPlatformVersionInfoText(config, { forceRefresh: true });
  const header = 'TRAZiT Components Version Info:';
  console.warn(`${header}\n${text}`);
  console.log(`${header}\n${text}`);
  return text;
}

export function getCachedPlatformVersionInfo() {
  return _cachedText && !isLikelyHtmlResponse(_cachedText) ? _cachedText : null;
}

export function invalidatePlatformVersionInfoCache() {
  _cachedText = null;
}
